import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Modal,
  Alert,
  InputGroup,
  Spinner,
  Collapse,
} from 'react-bootstrap';
import {
  Plus,
  PencilSquare,
  Trash,
  Search,
  ChevronDown,
  ChevronRight,
} from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import { tenMuc, tenNhom } from '../../assets/data/tenCauHinh';
import { useBranch, useBranchAPI } from '../../hooks';
import { configurationAPI } from '../../services';
import { toast } from 'react-toastify';

const TYPE_OPTIONS = ['NUMBER', 'STRING', 'JSON', 'BOOLEAN', 'ARRAY'];

// Tên tiếng Việt của kiểu dữ liệu — chữ cho người đọc, giá trị lưu vẫn giữ nguyên tên gốc.
const TEN_KIEU = {
  NUMBER: 'Số',
  STRING: 'Chữ',
  JSON: 'Bảng dữ liệu',
  BOOLEAN: 'Bật/tắt',
  ARRAY: 'Danh sách',
};

// #115005/#120646 — nhóm cờ tính năng (quy ước #201153) KHÔNG còn hiện ở màn này nữa; các công tắc
// bật/tắt tính năng mới đã dời sang menu riêng "Tính Năng Mới" (Quản lý hệ thống).
const NHOM_CO_TINH_NANG = 'FEATURE_FLAG';

const CATEGORY_OPTIONS = [
  'POINT',
  'ATTENDANCE',
  'PAYROLL',
  'SHIFT',
  'PERFORMANCE',
  'EMPLOYEE',
  NHOM_CO_TINH_NANG,
];

// Bộ lọc không cho chọn nhóm cờ tính năng: lọc ra cũng không còn mục nào ở màn này.
const CATEGORY_FILTER_OPTIONS = CATEGORY_OPTIONS.filter((c) => c !== NHOM_CO_TINH_NANG);

export default function ConfigurationPage() {
  const { selectedBranch, branches } = useBranch();
  const { withBranch } = useBranchAPI();

  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState(() => selectedBranch?.id || '');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    type: 'STRING',
    category: 'POINT',
    description: '',
    branch_id: null,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  // Bộ lọc thu vào một dòng, bấm mới mở — trên điện thoại đỡ chiếm mất 5 dòng đầu màn.
  const [moBoLoc, setMoBoLoc] = useState(false);

  useEffect(() => {
    setBranchFilter(selectedBranch?.id || '');
  }, [selectedBranch]);

  useEffect(() => {
    loadConfigurations();
  }, [categoryFilter, branchFilter, isActiveFilter, typeFilter, searchTerm]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        category: categoryFilter || '',
        branch_id: branchFilter || '',
        is_active: isActiveFilter || '',
        type: typeFilter || '',
        search: searchTerm || '',
      };
      const res = await configurationAPI.getConfigurations(params);
      setConfigurations(res.data || res || []);
    } catch (err) {
      console.error('Error loading configurations:', err);
      setError('Không thể tải danh sách cấu hình');
      toast.error(err?.message || 'Không thể tải danh sách cấu hình');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingConfig(null);
    setFormData({
      key: '',
      value: '',
      type: 'STRING',
      category: 'POINT',
      description: '',
      branch_id: selectedBranch?.id || null,
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      key: config.key,
      value: config.value,
      type: config.type,
      category: config.category,
      description: config.description || '',
      branch_id: config.branch_id || null,
      is_active: config.is_active !== undefined ? config.is_active : true,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingConfig) {
        // Update - không gửi key và type (không thể thay đổi)
        const { key, type, ...updateData } = formData;
        await configurationAPI.updateConfiguration(editingConfig.id, updateData);
        toast.success('Cập nhật cấu hình thành công');
      } else {
        // Create
        await configurationAPI.createConfiguration(formData);
        toast.success('Tạo cấu hình thành công');
      }
      setShowModal(false);
      loadConfigurations();
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast.error(err?.message || 'Lưu cấu hình thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (config) => {
    if (!window.confirm(`Bạn có chắc muốn xóa cấu hình "${config.key}"?`)) {
      return false;
    }
    try {
      await configurationAPI.deleteConfiguration(config.id);
      toast.success('Xóa cấu hình thành công');
      loadConfigurations();
      return true;
    } catch (err) {
      console.error('Error deleting configuration:', err);
      toast.error(err?.message || 'Xóa cấu hình thất bại');
      return false;
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await configurationAPI.toggleActive(config.id);
      toast.success(`Đã ${config.is_active ? 'tắt' : 'bật'} cấu hình`);
      loadConfigurations();
    } catch (err) {
      console.error('Error toggling configuration:', err);
      toast.error(err?.message || 'Thay đổi trạng thái thất bại');
    }
  };

  // #115005/#120646 — các công tắc tính năng mới đã dời sang menu riêng, không hiện ở màn này nữa.
  const mucHienThi = configurations.filter((c) => c.category !== NHOM_CO_TINH_NANG);

  // Gom theo nhóm, nhóm nhiều mục xếp trước, trong nhóm xếp theo tên khoá — đúng bản vẽ đã duyệt.
  const nhomHienThi = Object.entries(
    mucHienThi.reduce((gom, c) => {
      (gom[c.category] = gom[c.category] || []).push(c);
      return gom;
    }, {}),
  )
    .sort((a, b) => b[1].length - a[1].length)
    .map(([nhom, ds]) => [
      nhom,
      [...ds].sort((x, y) => x.key.toLowerCase().localeCompare(y.key.toLowerCase())),
    ]);

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mb-3">
            <h3 className="mb-1">Cấu Hình Hệ Thống</h3>
            <p className="text-muted mb-0">
              {loading ? 'Đang xem…' : `${mucHienThi.length} mục`} · Mỗi dòng nêu rõ tác dụng và có
              công tắc bật/tắt. Muốn đổi giá trị thì bấm <b>Sửa</b>.
            </p>
          </div>

          {/* Người quen lọc nhóm cờ tính năng ở đây sẽ không thấy nữa — chỉ đường sang menu mới. */}
          <Alert
            variant="warning"
            className="py-2"
            data-testid="chi-duong-tinh-nang-moi"
          >
            <b>Đã tách ra:</b> các công tắc bật/tắt tính năng mới không còn nằm ở đây — xem menu{' '}
            <b>Quản lý hệ thống → Tính Năng Mới</b>.
          </Alert>
        </Col>

        <Col xl={12}>
          <Card className="mb-3">
            <Card.Body className="py-2">
              <Button
                variant="link"
                className="p-0 text-decoration-none fw-semibold"
                onClick={() => setMoBoLoc((v) => !v)}
                aria-expanded={moBoLoc}
                data-testid="nut-bo-loc"
              >
                {moBoLoc ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Bộ lọc
              </Button>
              <Collapse in={moBoLoc}>
                <div data-testid="khoi-bo-loc">
                  <Row className="mt-2">
                    <Col md={3}>
                      <Form.Label>Tìm kiếm</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <Search size={16} />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Tìm theo tên khoá hoặc mô tả…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ fontSize: 16 }}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Label>Nhóm</Form.Label>
                      <Form.Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        style={{ fontSize: 16 }}
                      >
                        <option value="">Tất cả</option>
                        {CATEGORY_FILTER_OPTIONS.map((cat) => (
                          <option
                            key={cat}
                            value={cat}
                          >
                            {tenNhom(cat)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Trạng thái</Form.Label>
                      <Form.Select
                        value={isActiveFilter}
                        onChange={(e) => setIsActiveFilter(e.target.value)}
                        style={{ fontSize: 16 }}
                      >
                        <option value="">Tất cả</option>
                        <option value="true">Đang bật</option>
                        <option value="false">Đang tắt</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Kiểu dữ liệu</Form.Label>
                      <Form.Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={{ fontSize: 16 }}
                      >
                        <option value="">Tất cả</option>
                        {TYPE_OPTIONS.map((type) => (
                          <option
                            key={type}
                            value={type}
                          >
                            {TEN_KIEU[type] || type}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex align-items-end"
                    >
                      <Button
                        variant="outline-secondary"
                        className="mt-2 mt-md-0"
                        onClick={() => {
                          setCategoryFilter('');
                          setBranchFilter(selectedBranch?.id || '');
                          setIsActiveFilter('');
                          setTypeFilter('');
                          setSearchTerm('');
                        }}
                      >
                        Đặt lại
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={12}>
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="primary"
              onClick={openCreate}
              className="d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              Tạo cấu hình mới
            </Button>
          </div>

          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : mucHienThi.length === 0 ? (
            <Card>
              <Card.Body className="text-center text-muted py-5">Không có cấu hình nào</Card.Body>
            </Card>
          ) : (
            nhomHienThi.map(([nhom, ds]) => (
              <div key={nhom}>
                <div
                  className="text-uppercase text-muted fw-bold small mt-4 mb-2"
                  style={{ letterSpacing: '.04em' }}
                >
                  {tenNhom(nhom)} · {ds.length} mục
                </div>
                {ds.map((config) => (
                  <Card
                    key={config.id}
                    className="mb-2"
                    data-testid={`muc-cau-hinh-${config.key}`}
                  >
                    <Card.Body className="d-flex gap-3 align-items-start py-3">
                      <div
                        className="flex-grow-1"
                        style={{ minWidth: 0 }}
                      >
                        <div className="fw-semibold">{tenMuc(config.key)}</div>
                        <div className="my-1">
                          <Badge bg={config.branch_id ? 'warning' : 'info'}>
                            {config.branch?.name || (config.branch_id ? 'Chi nhánh' : 'Dùng chung')}
                          </Badge>
                        </div>
                        <div className="text-secondary small">
                          {config.description || '(chưa có mô tả — bấm Sửa để bổ sung)'}
                        </div>
                        <div
                          className="text-muted text-break mt-1"
                          style={{ fontSize: '.72rem', fontFamily: 'ui-monospace, Menlo, monospace' }}
                        >
                          {config.key}
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2">
                        <Form.Check
                          type="switch"
                          id={`cong-tac-${config.id}`}
                          checked={!!config.is_active}
                          onChange={() => handleToggleActive(config)}
                          title={config.is_active ? 'Đang bật — bấm để tắt' : 'Đang tắt — bấm để bật'}
                          data-testid={`cong-tac-${config.key}`}
                        />
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(config)}
                          className="d-flex align-items-center gap-1"
                          data-testid={`nut-sua-${config.key}`}
                        >
                          <PencilSquare size={14} />
                          Sửa
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            ))
          )}
        </Col>
      </Row>

      {/* Modal Create/Edit */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingConfig ? 'Sửa cấu hình' : 'Tạo cấu hình mới'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Key <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    disabled={!!editingConfig}
                    placeholder="config_key_name"
                    style={{ fontSize: 16 }}
                  />
                  <Form.Text className="text-muted">
                    {editingConfig
                      ? 'Key không thể thay đổi sau khi tạo'
                      : 'Key phải unique, tối đa 100 ký tự'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Kiểu dữ liệu <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    disabled={!!editingConfig}
                  >
                    {TYPE_OPTIONS.map((type) => (
                      <option
                        key={type}
                        value={type}
                      >
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {editingConfig ? 'Kiểu dữ liệu không thể thay đổi' : 'Chọn kiểu dữ liệu'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Mô tả — câu này là câu hiện ở danh sách ngoài</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Mục này dùng để làm gì, người đọc cần biết điều gì"
                style={{ fontSize: 16 }}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Danh mục <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option
                        key={cat}
                        value={cat}
                      >
                        {cat}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chi nhánh</Form.Label>
                  <Form.Select
                    value={formData.branch_id || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        branch_id: e.target.value || null,
                      })
                    }
                  >
                    <option value="">Global (Tất cả chi nhánh)</option>
                    {branches?.map((branch) => (
                      <option
                        key={branch.id}
                        value={branch.id}
                      >
                        {branch.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Giá trị <span className="text-danger">*</span>
              </Form.Label>
              {formData.type === 'JSON' || formData.type === 'ARRAY' ? (
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  style={{ fontSize: 16 }}
                  placeholder={
                    formData.type === 'JSON'
                      ? '{"key": "value"}'
                      : '["item1", "item2"]'
                  }
                />
              ) : formData.type === 'BOOLEAN' ? (
                <Form.Select
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </Form.Select>
              ) : (
                <Form.Control
                  type={formData.type === 'NUMBER' ? 'number' : 'text'}
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="Nhập giá trị"
                  style={{ fontSize: 16 }}
                />
              )}
              <Form.Text className="text-muted">
                {formData.type === 'JSON' || formData.type === 'ARRAY'
                  ? 'Nhập JSON hợp lệ'
                  : formData.type === 'BOOLEAN'
                    ? 'Chọn true hoặc false'
                    : formData.type === 'NUMBER'
                      ? 'Nhập số hợp lệ'
                      : 'Nhập giá trị'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is-active"
                label="Đang hoạt động"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div>
            {editingConfig && (
              <Button
                variant="outline-danger"
                className="d-flex align-items-center gap-2"
                onClick={async () => {
                  const xong = await handleDelete(editingConfig);
                  if (xong) setShowModal(false);
                }}
                data-testid="nut-xoa-cau-hinh"
              >
                <Trash size={14} />
                Xoá mục này
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !formData.key || !formData.value || !formData.type || !formData.category}
          >
            {saving ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Đang lưu...
              </>
            ) : (
              'Lưu'
            )}
          </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}

